// src/components/eframes/EFrame248.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import {
  ResponsiveContainer,
  ResponsiveHeader,
  ResponsiveContent,
} from "../ui/ResponsiveLayout";
import { Card } from "../ui";
import { DocumentsIcon } from "../ui/Icons";
import type { AppDocument } from "../../types/document";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface Pet {
  id: string;
  name: string;
  type: "dog" | "cat" | "bird" | "fish" | "reptile" | "other" | "";
  breed: string;
  age: string;
  specialNeeds: string;
  veterinarian: string;
  veterinarianContact: string;
  preferredCaregiver: string;
  caregiverContact: string;
  careInstructions: string;
  diet?: string;
  medications?: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  documents?: AppDocument[];
}

interface Dependant {
  id: string;
  name: string;
  relationship: "child" | "parent" | "sibling" | "spouse" | "other" | "";
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents?: AppDocument[];
}

interface FinancialArrangements {
  petCareBudget: string;
  petCareInstructions: string;
  dependentCareBudget: string;
  dependentCareInstructions: string;
  trustFunds: string;
  insurancePolicies: string;
  emergencyFunds: string;
  specialInstructions: string;
  paymentSchedule: string;
  careCoordinator: string;
  careCoordinatorContact: string;
}

interface FinalInstructions {
  emergencyProcedures: string;
  documentLocations: string;
  importantContacts: string;
  finalMessage: string;
  specialRequests: string;
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const EFrame248: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [pets, setPets] = useState<Pet[]>([]);
  const [dependents, setDependents] = useState<Dependant[]>([]);
  const [financialArrangements, setFinancialArrangements] =
    useState<FinancialArrangements | null>(null);
  const [finalInstructions, setFinalInstructions] =
    useState<FinalInstructions | null>(null);

  const normalizePets = (input: Pet[]): Pet[] => {
    return (input || []).map((pet) => ({
      ...pet,
      documents: normalizeDocuments(pet.documents || []),
    }));
  };

  const normalizeDependents = (input: Dependant[]): Dependant[] => {
    return (input || []).map((dependent) => ({
      ...dependent,
      documents: normalizeDocuments(dependent.documents || []),
    }));
  };

  useEffect(() => {
    if (session?.taskProgress?.pets?.answers) {
      const answers = session.taskProgress.pets.answers;

      if (answers.petsAndDependants) {
        setPets(normalizePets(answers.petsAndDependants.pets || []));
        setDependents(normalizeDependents(answers.petsAndDependants.dependants || []));
      }

      if (answers.financialArrangements) {
        setFinancialArrangements(answers.financialArrangements);
      }

      if (answers.finalInstructions) {
        setFinalInstructions(answers.finalInstructions);
      }

      return;
    }
  }, [session?.taskProgress?.pets]);

  const capitalize = (str: string) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="space-y-8">
          <div className="mb-6 flex justify-start">
            <BackToExecutorDashboardButton />
          </div>
          <ResponsiveHeader title={`${planOwnerName}'s Pets and Dependents Care Plan`} />

          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Care Plan Overview
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center border-r border-stone-100">
                <div className="text-2xl font-bold text-cyan-900">
                  {pets.length}
                </div>
                <div className="text-sm text-stone-600 font-medium">
                  Pets Entered
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-700">
                  {dependents.length}
                </div>
                <div className="text-sm text-stone-600 font-medium">
                  Dependents Entered
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Pets</h3>
              <span className="text-sm text-stone-500">({pets.length})</span>
            </div>

            <div
              className={
                pets.length >= 3
                  ? "max-h-[200px] overflow-y-auto pr-2 custom-scrollbar"
                  : ""
              }
            >
              {pets.length > 0 ? (
                <div className="space-y-3">
                  {pets.map((pet) => {
                    const attachedDocs = normalizeDocuments(pet.documents || []);

                    return (
                      <div key={pet.id} className="bg-stone-50 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="font-medium text-stone-800">
                            {pet.name}
                          </div>
                          <span className="text-[13px] text-stone-700 px-2 py-0.5">
                            {capitalize(pet.type)}
                          </span>
                        </div>

                        <div className="text-sm text-stone-600 mt-1">
                          {pet.age && `Age: ${pet.age}`}
                          {pet.breed && ` • ${pet.breed}`}
                        </div>

                        {attachedDocs.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {attachedDocs.map((doc, docIndex) => (
                              <div
                                key={`${doc.id}-${docIndex}`}
                                className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                <span
                                  className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No pets available.</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Dependents
              </h3>
              <span className="text-sm text-stone-500">
                ({dependents.length})
              </span>
            </div>

            <div
              className={
                dependents.length >= 3
                  ? "max-h-[200px] overflow-y-auto pr-2 custom-scrollbar"
                  : ""
              }
            >
              {dependents.length > 0 ? (
                <div className="space-y-3">
                  {dependents.map((dependent) => {
                    const attachedDocs = normalizeDocuments(dependent.documents || []);

                    return (
                      <div
                        key={dependent.id}
                        className="bg-stone-50 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start">
                          <div className="font-medium text-stone-800">
                            {dependent.name}
                          </div>
                          <span className="text-[13px] text-stone-700">
                            {capitalize(dependent.relationship)}
                          </span>
                        </div>

                        <div className="text-sm text-stone-600 mt-1">
                          {dependent.age && `Age: ${dependent.age}`}
                          {dependent.preferredGuardian &&
                            ` • Guardian: ${dependent.preferredGuardian}`}
                        </div>

                        {attachedDocs.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {attachedDocs.map((doc, docIndex) => (
                              <div
                                key={`${doc.id}-${docIndex}`}
                                className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                <span
                                  className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No dependents available.</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-stone-800">
                Financial Arrangements
              </h2>
            </div>

            <div className="max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
              {financialArrangements?.petCareBudget?.trim() ||
              financialArrangements?.dependentCareBudget?.trim() ||
              financialArrangements?.trustFunds?.trim() ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-2 text-sm text-stone-600">
                    {financialArrangements.petCareBudget?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Pet Care Budget:
                        </span>{" "}
                        {financialArrangements.petCareBudget}
                      </p>
                    )}

                    {financialArrangements.petCareInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Pet Care Instructions:
                        </span>{" "}
                        {financialArrangements.petCareInstructions}
                      </p>
                    )}

                    {financialArrangements.dependentCareBudget?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Dependent Care Budget:
                        </span>{" "}
                        {financialArrangements.dependentCareBudget}
                      </p>
                    )}

                    {financialArrangements.dependentCareInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Dependent Care Instructions:
                        </span>{" "}
                        {financialArrangements.dependentCareInstructions}
                      </p>
                    )}

                    {financialArrangements.trustFunds?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Trust Funds:
                        </span>{" "}
                        {financialArrangements.trustFunds}
                      </p>
                    )}

                    {financialArrangements.insurancePolicies?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Insurance Policies:
                        </span>{" "}
                        {financialArrangements.insurancePolicies}
                      </p>
                    )}

                    {financialArrangements.emergencyFunds?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Emergency Funds:
                        </span>{" "}
                        {financialArrangements.emergencyFunds}
                      </p>
                    )}

                    {financialArrangements.paymentSchedule?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Payment Schedule:
                        </span>{" "}
                        {financialArrangements.paymentSchedule}
                      </p>
                    )}

                    {financialArrangements.careCoordinator?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Care Coordinator:
                        </span>{" "}
                        {financialArrangements.careCoordinator}{" "}
                        {financialArrangements.careCoordinatorContact
                          ? `(${financialArrangements.careCoordinatorContact})`
                          : ""}
                      </p>
                    )}

                    {financialArrangements.specialInstructions?.trim() && (
                      <p>
                        <span className="font-semibold text-stone-700">
                          Special Instructions:
                        </span>{" "}
                        {financialArrangements.specialInstructions}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No financial arrangements available.</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-stone-800">
                Final Instructions
              </h2>
            </div>

            <div className="max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
              {finalInstructions?.emergencyProcedures?.trim() ||
              finalInstructions?.documentLocations?.trim() ||
              finalInstructions?.importantContacts?.trim() ||
              finalInstructions?.finalMessage?.trim() ||
              finalInstructions?.specialRequests?.trim() ? (
                <div className="space-y-4 bg-stone-50 rounded-lg p-4">
                  {finalInstructions.emergencyProcedures?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Emergency Procedures
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.emergencyProcedures}
                      </p>
                    </div>
                  )}

                  {finalInstructions.documentLocations?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Document Locations
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.documentLocations}
                      </p>
                    </div>
                  )}

                  {finalInstructions.importantContacts?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Important Contacts
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.importantContacts}
                      </p>
                    </div>
                  )}

                  {finalInstructions.finalMessage?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Final Message
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.finalMessage}
                      </p>
                    </div>
                  )}

                  {finalInstructions.specialRequests?.trim() && (
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        Special Requests
                      </p>
                      <p className="text-sm text-stone-600">
                        {finalInstructions.specialRequests}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No final instructions available.</p>
                </div>
              )}
            </div>
          </Card>
          <div className="mt-8 flex justify-end border-t border-stone-200 pt-6">
            <BackToExecutorDashboardButton />
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame248;